import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Client-side validation
    if (!formData.name.trim()) {
      setStatus({ submitting: false, success: false, error: 'Venligst indtast dit navn.' });
      return;
    }
    if (!formData.phone.trim()) {
      setStatus({ submitting: false, success: false, error: 'Venligst indtast dit telefonnummer.' });
      return;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setStatus({ submitting: false, success: false, error: 'Venligst indtast en gyldig e-mailadresse.' });
      return;
    }
    if (!formData.description.trim()) {
      setStatus({ submitting: false, success: false, error: 'Venligst indtast en beskrivelse af din opgave.' });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Kunne ikke oprette forbindelse til serveren. Prøv igen senere.',
      });
    }
  };

  return (
    <div className="bg-white border border-[#E2E2E0] rounded-lg p-6 sm:p-8 shadow-sm">
      <h3 className="text-xl font-bold text-[#1C1E1D] mb-2 font-display">Få et uforpligtende tilbud</h3>
      <p className="text-sm text-[#1C1E1D]/70 mb-6">
        Udfyld formularen nedenfor, så vender vi tilbage til dig med et uforpligtende tilbud eller kontakter dig for en uddybende snak.
      </p>

      {status.success ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center">
          <i className="fa-solid fa-circle-check text-4xl text-emerald-600 mb-3" />
          <h4 className="text-lg font-bold text-emerald-900">Tak for din henvendelse!</h4>
          <p className="text-sm text-emerald-800 mt-2">
            Vi har modtaget din besked og vil vende tilbage til dig hurtigst muligt, typisk inden for 24 timer på hverdage.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-[#163A2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1C1E1D] transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-[#1C1E1D] mb-1">
              Navn <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#1C1E1D]/40">
                <i className="fa-solid fa-user text-sm" />
              </span>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full rounded-md border border-[#E2E2E0] bg-[#F4F1EA]/20 py-2.5 pl-10 pr-4 text-sm text-[#1C1E1D] placeholder-[#1C1E1D]/40 focus:border-[#163A2B] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#163A2B] transition-all"
              />
            </div>
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-[#1C1E1D] mb-1">
              Telefonnummer <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#1C1E1D]/40">
                <i className="fa-solid fa-phone text-sm" />
              </span>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Tlf. f.eks. 30 71 33 11"
                className="w-full rounded-md border border-[#E2E2E0] bg-[#F4F1EA]/20 py-2.5 pl-10 pr-4 text-sm text-[#1C1E1D] placeholder-[#1C1E1D]/40 focus:border-[#163A2B] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#163A2B] transition-all"
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-[#1C1E1D] mb-1">
              E-mailadresse <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#1C1E1D]/40">
                <i className="fa-solid fa-envelope text-sm" />
              </span>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full rounded-md border border-[#E2E2E0] bg-[#F4F1EA]/20 py-2.5 pl-10 pr-4 text-sm text-[#1C1E1D] placeholder-[#1C1E1D]/40 focus:border-[#163A2B] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#163A2B] transition-all"
              />
            </div>
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-[#1C1E1D] mb-1">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskriv dit projekt... F.eks. ny indkørsel på ca. 80 kvm, støbning af fundament til garage, eller murerarbejde."
              className="w-full rounded-md border border-[#E2E2E0] bg-[#F4F1EA]/20 py-2.5 px-4 text-sm text-[#1C1E1D] placeholder-[#1C1E1D]/40 focus:border-[#163A2B] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#163A2B] transition-all resize-y"
            ></textarea>
          </div>

          {/* Error Message */}
          {status.error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation text-red-600" />
              <span>{status.error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full inline-flex items-center justify-center rounded-md bg-[#163A2B] px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-[#1C1E1D] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2" />
                Sender besked...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane mr-2" />
                Send henvendelse
              </>
            )}
          </button>

          <p className="text-[11px] text-center text-[#1C1E1D]/50 mt-2">
            Vi behandler dine oplysninger fortroligt og bruger dem kun til at kontakte dig vedrørende dit tilbud.
          </p>
        </form>
      )}
    </div>
  );
}
